import React from 'react'
import { cn } from '@/lib/utils'

export interface ContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Maximum width of the container
   * @default '7xl'
   */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full'
  /**
   * Whether to center the container
   * @default true
   */
  center?: boolean
  /**
   * Custom className for additional styling
   */
  className?: string
  /**
   * Child elements
   */
  children?: React.ReactNode
}

/**
 * Container component for responsive layout with padding and max-width
 *
 * Replacement for MUI Container with Tailwind CSS styling
 *
 * @example
 * ```tsx
 * <Container>
 *   <Text variant="h1">Welcome</Text>
 * </Container>
 * ```
 *
 * @example
 * ```tsx
 * <Container maxWidth="lg" center={false}>
 *   <Text variant="body1">Content here</Text>
 * </Container>
 * ```
 */
export const Container = React.forwardRef<HTMLDivElement, ContainerProps>(
  ({ maxWidth = '7xl', center = true, className, children, ...props }, ref) => {
    const maxWidthClasses = {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-xl',
      '2xl': 'max-w-2xl',
      '3xl': 'max-w-3xl',
      '4xl': 'max-w-4xl',
      '5xl': 'max-w-5xl',
      '6xl': 'max-w-6xl',
      '7xl': 'max-w-7xl',
      full: 'max-w-full'
    }

    return (
      <div
        ref={ref}
        className={cn(
          'w-full px-4 md:px-6 lg:px-8',
          maxWidthClasses[maxWidth],
          center && 'mx-auto',
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Container.displayName = 'Container'
